import type { AgentAttributionTransactionMeta } from '@atlaskit/editor-common/transaction-agent-attribution';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { CONFIG_ITEM_KEYS } from '../../ui/prebuilt/config-items/config-item-keys';
import { SupportedTranslationLanguages } from '../../ui/prebuilt/config-items/prompts/translate/types';

/**
 * Attribution applied to Quick Action edits.
 *
 * PLACEHOLDER - do not enable `platform_editor_ai_quick_actions_agent_attribution` on the
 * back of this value. Consumers read `agentType: 'convo-ai'` with no `agentId` as Rovo Chat,
 * because `agentId` is only populated for Studio agents. Enabling the gate today would
 * attribute Quick Action edits to Rovo.
 *
 * Quick Actions are instead to be identified by a dedicated `agentTrigger` property on the
 * step - `"fe" | "be" | "quick-actions"`, labels TBD - which is independent of agent context
 * and does not map to `AgentContextService`. `agentType` is not suitable because it models the
 * actor, and a Quick Action is not an actor. Once `agentTrigger` exists this constant should
 * be replaced by it.
 *
 * @see https://hello.atlassian.net/wiki/spaces/EDITOR/pages/7556910519
 */
const QUICK_ACTION_AGENT_ATTRIBUTION: AgentAttributionTransactionMeta = {
	agentType: 'convo-ai',
};

/**
 * Translation config item keys are built at runtime as `Translate to ${language}` in
 * `prompts/translate/translate.tsx`. Deriving them the same way here keeps this set in sync
 * when a language is added, rather than duplicating the `TRANSLATE_TO_*` enum members.
 */
const translationConfigItemKeys = Object.values(SupportedTranslationLanguages).map(
	(language) => `Translate to ${language}` as CONFIG_ITEM_KEYS,
);

/**
 * Config items surfaced as Confluence Quick Actions - the selection toolbar transforms that
 * rewrite existing content in place.
 */
const QUICK_ACTION_CONFIG_ITEM_KEYS: ReadonlySet<CONFIG_ITEM_KEYS> = new Set<CONFIG_ITEM_KEYS>([
	CONFIG_ITEM_KEYS.IMPROVE_WRITING,
	CONFIG_ITEM_KEYS.ADD_POLISH,
	CONFIG_ITEM_KEYS.FIX_SPELLING_AND_GRAMMAR,
	CONFIG_ITEM_KEYS.MAKE_LONGER,
	CONFIG_ITEM_KEYS.MAKE_SHORTER,
	CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CASUAL,
	CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EDUCATIONAL,
	CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EMPATHETIC,
	CONFIG_ITEM_KEYS.CHANGE_TONE_TO_NEUTRAL,
	CONFIG_ITEM_KEYS.CHANGE_TONE_TO_PROFESSIONAL,
	...translationConfigItemKeys,
]);

export const isQuickActionConfigItem = (configItemKey?: CONFIG_ITEM_KEYS): boolean => {
	if (!configItemKey) {
		return false;
	}

	return QUICK_ACTION_CONFIG_ITEM_KEYS.has(configItemKey);
};

/**
 * Resolves the agent attribution stamped onto an AI streaming transaction, which
 * `collab-provider` serialises onto the outgoing step so NCS can raise agent presence.
 *
 * A session-scoped attribution always wins - that is supplied by the Rovo bridge for
 * frontend streaming and already identifies a concrete agent. Quick Actions never start such
 * a session, so behind `platform_editor_ai_quick_actions_agent_attribution` they fall back to
 * a Convo AI attribution. With that gate off the result is identical to the session-only
 * behaviour that preceded it.
 */
export const resolveAgentAttribution = ({
	sessionAgentAttribution,
	configItemKey,
}: {
	configItemKey?: CONFIG_ITEM_KEYS;
	sessionAgentAttribution?: AgentAttributionTransactionMeta;
}): AgentAttributionTransactionMeta | undefined => {
	if (!fg('platform_editor_agentic_step_attribution')) {
		return undefined;
	}

	if (sessionAgentAttribution) {
		return sessionAgentAttribution;
	}

	if (!fg('platform_editor_ai_quick_actions_agent_attribution')) {
		return undefined;
	}

	return isQuickActionConfigItem(configItemKey) ? QUICK_ACTION_AGENT_ATTRIBUTION : undefined;
};
